import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { settingsService } from 'v2/shared/services/settings-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { getSettingsQueryKeys, onboardingMutationKeys } from 'v2/shared/utils/query-key-management'
import type { RUserProfile } from 'v2/types/api/settings'
import { errorToast } from 'v2/utils/toasts'
import { latchOnboardingCompleted } from './onboarding-completed'
import type { OnboardingFormData } from './types'

export const useCompleteOnboardingMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    // OnboardingGate reads this key to tell "this tab submitted" from "a sibling tab did".
    mutationKey: onboardingMutationKeys().complete,
    mutationFn: (payload: OnboardingFormData) => settingsService.completeOnboarding(payload),
    // The org this POST is for, read when it starts: a sibling tab's org switch or logout
    // removes every query and broadcasts another org's profile in while it is in flight.
    onMutate: () => ({
      orgId: queryClient.getQueryData<RUserProfile>(getSettingsQueryKeys().profile)
        ?.organizations?.[0]?.org_id,
    }),
    // OnboardingGate (mounted in shared/layouts/main.tsx) gates on this cached flag, so flip it
    // here instead of waiting on the invalidation below — otherwise the post-success
    // navigation lands on a product page whose redirect reads the stale `false` and
    // bounces the user straight back into the wizard. Same SER-6080 class as the legacy
    // questionnaire: 30 of 625 prod submits were repeats, one session posting 17x in 12.6s.
    onSuccess: async (_data, _payload, context) => {
      // Latch FIRST (SER-6449), before any await: cache notifications are batched via
      // setTimeout(0), so a sibling's stale replay can land before a render would latch.
      latchOnboardingCompleted(context?.orgId)
      // Then cancel this tab's own in-flight profile GET: one that read the row pre-commit
      // would overwrite the `true` below (the refill's resubmit skips the POST, so logs never show it).
      await queryClient.cancelQueries({ queryKey: getSettingsQueryKeys().profile })
      queryClient.setQueryData<RUserProfile>(getSettingsQueryKeys().profile, (prev) => {
        if (!prev?.organizations?.length) return prev
        // Same org guard as the latch: if a sibling's switch already swapped the cached
        // profile, the other org must not be marked onboarded here either.
        const submitted = (org: RUserProfile['organizations'][number]) =>
          !context?.orgId || org.org_id === context.orgId
        return {
          ...prev,
          organizations: prev.organizations.map((org, index) =>
            index === 0 && submitted(org) ? { ...org, onboarding_completed: true } : org,
          ),
        }
      })
    },
    // The response interceptor deliberately swallows 4xx (create-service.ts), so without
    // this a failed submit is completely silent — no toast, no log, nothing on screen.
    onError: (error) => {
      errorToast({
        title: 'Could not save your answers',
        description: getErrorText(error as AxiosError) || 'Please try again.',
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: getSettingsQueryKeys().profile })
    },
  })
}
